import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1Questions,
  exemptionQuestionsFromData,
  allowanceAndAdditionalAmtQuestions,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  // Basic info questions
  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  // Total Allowances + Additional Amount Withheld Questions (Line 4 and 5)
  const allowanceAndAdditionalAmtForm = useRef();
  const allowanceAndAdditionalAmtInitQ = getInitialQuestions(allowanceAndAdditionalAmtQuestions);
  const [allowanceAndAdditionalAmt, setAllowanceAndAdditionalAmt] = useState(
    allowanceAndAdditionalAmtInitQ,
  );

  // Exemption questions - Q3
  const exemptionForm = useRef();
  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);
  const exemptionsInitialQuestions = getInitialQuestions(exemptionQuestionsFromData);
  const [exemptions, setExemptions] = useState(exemptionsInitialQuestions);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding. (Line 3)
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowanceAndAdditionalAmtQuestions = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={allowanceAndAdditionalAmt}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = allowanceAndAdditionalAmt.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowanceAndAdditionalAmt(questions);
        }}
        ref={allowanceAndAdditionalAmtForm}
      />
    </div>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={exemptions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptions(questions);
        }}
        ref={exemptionForm}
      />
    </div>
  );

  const handleValidation = () => {
    // Validate basic info questions
    if (step1Form && step1Form.current
      && step1Form.current.validate((error) => !!error)) {
      return true;
    }

    if (!isExemptFromWithholding) {
    // If not exempt from withholding, validate allowance and additional amount questions
      if (allowanceAndAdditionalAmtForm && allowanceAndAdditionalAmtForm.current
        && allowanceAndAdditionalAmtForm.current.validate((error) => !!error)) {
        return true;
      }
    // If exempt from withholding, validate exemption reason
    } else if (exemptionForm && exemptionForm.current
        && exemptionForm.current.validate((error) => !!error)) {
      return true;
    }

    // If all validation passes, return false
    return false;
  };

  const handleSumission = () => {
    const hasErrors = handleValidation();
    if (hasErrors) return;

    const stepOneData = stepOneQuestions
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    const employeeFullName = [
      stepOneData.employee_first_name,
      stepOneData.employee_middle_name || '',
      stepOneData.employee_last_name,
    ].filter(Boolean).join(' ');

    // Initialise data
    let allowanceAndAdditionalAmtData = resetInitialQuestions(allowanceAndAdditionalAmtQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let exemptionData = resetInitialQuestions(exemptionQuestionsFromData)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    if (isExemptFromWithholding) {
      exemptionData = exemptions
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    } else {
      allowanceAndAdditionalAmtData = allowanceAndAdditionalAmt
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    }

    // Save data
    const payload = {
      ...stepOneData,
      ...allowanceAndAdditionalAmtData,
      ...exemptionData,
      is_exempt: isExemptFromWithholding,
      employee_full_name: employeeFullName,
    };

    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderAllowanceAndAdditionalAmtQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSumission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderAllowanceAndAdditionalAmtQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2025;
